import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import CategoryIcon from "@mui/icons-material/Category";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 25px rgba(33, 150, 243, 0.6); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function ChildCategoryManagement() {
  const [childCategories, setChildCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filterSubCategories, setFilterSubCategories] = useState([]); // for filter dropdown
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteChildCategoryName, setDeleteChildCategoryName] = useState("");
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Filter states
  const [filterChildName, setFilterChildName] = useState("");
  const [filterMainCategory, setFilterMainCategory] = useState("");
  const [filterSubCategory, setFilterSubCategory] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    mainCategory: "",
    subCategory: "",
    sortOrder: "",
    status: "active",
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState("");

  const token = localStorage.getItem("token");

  // Fetch all child categories
  const fetchChildCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/childcategory/get-all-childcategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setChildCategories(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch child categories", "error");
      }
    } catch (error) {
      console.error("Error fetching child categories:", error);
      showSnackbar(error.response?.data?.message || "Error fetching child categories", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch main categories
  const fetchMainCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/main-category/get-all-main-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setMainCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching main categories:", error);
    }
  };

  const fetchSubCategories = async (mainCategoryId) => {
    if (!mainCategoryId) {
      setSubCategories([]);
      return;
    }
    try {
      const response = await axios.get(`${BASE_URL}/subcategory/get-all-subcategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const filtered = response.data.data.filter(
          (sub) => sub.mainCategory?._id === mainCategoryId || sub.mainCategory === mainCategoryId
        );
        setSubCategories(filtered);
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
    }
  };

  // Fetch sub categories based on main category
  useEffect(() => {
    const fetchFilterSubCategories = async () => {
      if (!filterMainCategory) {
        setFilterSubCategories([]);
        setFilterSubCategory(""); // clear selected subcategory
        return;
      }
      try {
        const response = await axios.get(`${BASE_URL}/subcategory/get-all-subcategories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          const filtered = response.data.data.filter(
            (sub) =>
              sub.mainCategory?._id === filterMainCategory ||
              sub.mainCategory === filterMainCategory
          );
          setFilterSubCategories(filtered);
          // Reset subcategory filter when main category changes
          setFilterSubCategory("");
        }
      } catch (error) {
        console.error("Error fetching sub categories for filter:", error);
      }
    };
    fetchFilterSubCategories();
  }, [filterMainCategory, token]);

  useEffect(() => {
    fetchChildCategories();
    fetchMainCategories();
  }, []);

  // Handle main category change in form
  const handleMainCategoryChange = (mainCategoryId) => {
    setFormData((prev) => ({
      ...prev,
      mainCategory: mainCategoryId,
      subCategory: "", // Reset sub category when main category changes
    }));
    fetchSubCategories(mainCategoryId);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mainCategory") {
      handleMainCategoryChange(value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
      if (!validTypes.includes(file.type)) {
        showSnackbar("Please select a valid image (JPEG, PNG, WEBP)", "warning");
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("Image size should be less than 2MB", "warning");
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Handle form dialog open
  const handleOpenDialog = async (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setFormData({
        name: item.name || "",
        description: item.description || "",
        mainCategory: item.mainCategory?._id || item.mainCategory || "",
        subCategory: item.subCategory?._id || item.subCategory || "",
        sortOrder: item.sortOrder || "",
        status: item.status || "active",
      });
      setExistingImage(item.image || "");
      setImagePreview(item.image || null);
      setSelectedImage(null);

      // Fetch sub categories for the selected main category
      if (item.mainCategory?._id || item.mainCategory) {
        await fetchSubCategories(item.mainCategory?._id || item.mainCategory);
      }
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      mainCategory: "",
      subCategory: "",
      sortOrder: "",
      status: "active",
    });
    setSelectedImage(null);
    setImagePreview(null);
    setExistingImage("");
    setSelectedId(null);
    setSubCategories([]);
    // setFilteredSubCategories([]);
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar("Child category name is required", "warning");
      return;
    }
    // if (!formData.description.trim()) {
    //   showSnackbar("Description is required", "warning");
    //   return;
    // }
    // if (!formData.mainCategory) {
    //   showSnackbar("Please select a main category", "warning");
    //   return;
    // }
    // if (!formData.subCategory) {
    //   showSnackbar("Please select a sub category", "warning");
    //   return;
    // }
    if (!formData.sortOrder) {
      showSnackbar("Sort order is required", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);
      // formDataToSend.append("mainCategory", formData.mainCategory);
      // formDataToSend.append("subCategory", formData.subCategory);
      formDataToSend.append("sortOrder", formData.sortOrder);
      formDataToSend.append("status", formData.status);

      if (selectedImage) {
        formDataToSend.append("image", selectedImage);
      }

      if (formMode === "add") {
        response = await axios.post(
          `${BASE_URL}/childcategory/create-childcategory`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        response = await axios.put(
          `${BASE_URL}/childcategory/update-childcategory?id=${selectedId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      if (response.data.success) {
        showSnackbar(
          `Child category ${formMode === "add" ? "added" : "updated"} successfully!`,
          "success"
        );
        fetchChildCategories();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete dialog open
  const handleOpenDeleteDialog = (id, name) => {
    setDeleteId(id);
    setDeleteChildCategoryName(name);
    setDeleteDialogOpen(true);
  };

  // Handle delete child category
  const handleDeleteChildCategory = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(
        `${BASE_URL}/childcategory/delete-childcategory?id=${deleteId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        showSnackbar("Child category deleted successfully!", "success");
        fetchChildCategories();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("Error deleting child category:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete child category", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Clear filters
  const clearFilters = () => {
    setFilterChildName("");
    setFilterMainCategory("");
    setFilterSubCategory("");
  };

  // Filtered child categories
  const filteredChildCategories = useMemo(() => {
    let filtered = [...childCategories];

    if (filterChildName) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(filterChildName.toLowerCase())
      );
    }

    if (filterMainCategory) {
      filtered = filtered.filter(
        (item) =>
          item.mainCategory?._id === filterMainCategory || item.mainCategory === filterMainCategory
      );
    }

    if (filterSubCategory) {
      filtered = filtered.filter(
        (item) =>
          item.subCategory?._id === filterSubCategory || item.subCategory === filterSubCategory
      );
    }

    return filtered;
  }, [childCategories, filterChildName, filterMainCategory, filterSubCategory]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Get status chip
  const getStatusChip = (status) => {
    return status === "active" ? (
      <Chip
        label="ACTIVE"
        size="small"
        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: "bold" }}
      />
    ) : (
      <Chip
        label="INACTIVE"
        size="small"
        sx={{ bgcolor: "#ffebee", color: "#d32f2f", fontWeight: "bold" }}
      />
    );
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          IMAGE
        </MDTypography>
      ),
      accessor: "image",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CHILD CATEGORY
        </MDTypography>
      ),
      accessor: "name",
      align: "left",
    },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       MAIN CATEGORY
    //     </MDTypography>
    //   ),
    //   accessor: "mainCategory",
    //   align: "left",
    //   width: "15%",
    // },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       SUB CATEGORY
    //     </MDTypography>
    //   ),
    //   accessor: "subCategory",
    //   align: "left",
    //   width: "15%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          DESCRIPTION
        </MDTypography>
      ),
      accessor: "description",
      align: "left",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          SORT
        </MDTypography>
      ),
      accessor: "sortOrder",
      align: "center",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          STATUS
        </MDTypography>
      ),
      accessor: "status",
      align: "center",
    },
    // {
    //   Header: (
    //     <MDTypography variant="caption" fontWeight="bold">
    //       CREATED
    //     </MDTypography>
    //   ),
    //   accessor: "createdAt",
    //   align: "center",
    //   width: "8%",
    // },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
    },
  ];

  const rows = useMemo(
    () =>
      filteredChildCategories.map((childCategory) => ({
        image: (
          <Avatar
            src={childCategory.image || "/default-category.png"}
            alt={childCategory.name}
            sx={{
              width: 50,
              height: 50,
              borderRadius: "12px",
              mx: "auto",
              transition: "transform 0.3s",
              "&:hover": { transform: "scale(1.1)" },
            }}
          >
            {!childCategory.image && <SubdirectoryArrowRightIcon />}
          </Avatar>
        ),
        name: (
          <MDTypography variant="button" fontWeight="bold" color="dark">
            {childCategory.name}
          </MDTypography>
        ),
        mainCategory: (
          <MDTypography variant="caption" fontWeight="medium" color="primary">
            {childCategory.mainCategory?.name || "N/A"}
          </MDTypography>
        ),
        subCategory: (
          <MDTypography variant="caption" fontWeight="medium" color="secondary">
            {childCategory.subCategory?.name || "N/A"}
          </MDTypography>
        ),
        description: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {childCategory.description?.length > 60
              ? `${childCategory.description.substring(0, 60)}...`
              : childCategory.description}
          </MDTypography>
        ),
        sortOrder: (
          <MDTypography variant="button" fontWeight="medium" textAlign="center">
            {childCategory.sortOrder}
          </MDTypography>
        ),
        status: <MDBox textAlign="center">{getStatusChip(childCategory.status)}</MDBox>,
        action: (
          <MDBox display="flex" justifyContent="center" gap={0}>
            <Tooltip title="Edit Child Category">
              <IconButton
                color="info"
                onClick={() => handleOpenDialog("edit", childCategory)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Child Category">
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(childCategory._id, childCategory.name)}
                size="small"
                sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        ),
      })),
    [filteredChildCategories]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <SubdirectoryArrowRightIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Child Category Management
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog("add")}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Add Child Category
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters Section - Always visible */}
              <Box sx={{ pt: 3, px: 3 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Search by Child Category"
                      value={filterChildName}
                      onChange={(e) => setFilterChildName(e.target.value)}
                      placeholder="Enter child category name..."
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ color: "#9e9e9e", mr: 1 }} />,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Main Category</InputLabel>
                      <Select
                        value={filterMainCategory}
                        onChange={(e) => setFilterMainCategory(e.target.value)}
                        label="Main Category"
                        sx={{ borderRadius: "10px", height: "40px" }}
                      >
                        <MenuItem value="">All Main Categories</MenuItem>
                        {mainCategories.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sub Category</InputLabel>
                      <Select
                        value={filterSubCategory}
                        onChange={(e) => setFilterSubCategory(e.target.value)}
                        label="Sub Category"
                        sx={{ borderRadius: "10px", height: "40px" }}
                        disabled={!filterMainCategory}
                      >
                        <MenuItem value="">All Sub Categories</MenuItem>
                        {filterSubCategories.map((sub) => (
                          <MenuItem key={sub._id} value={sub._id}>
                            {sub.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid> */}
                  <Grid item xs={12} md={3}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      fullWidth
                      sx={{ borderRadius: "10px", height: "40px" }}
                    >
                      Clear Filters
                    </MDButton>
                  </Grid>
                </Grid>
              </Box>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : filteredChildCategories.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No child categories found. Add your first child category.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* PREMIUM ADD/EDIT DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>

              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {formMode === "add" ? "Add Child Category" : "Update Child Category"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add"
                    ? "Create a new child category"
                    : "Edit existing child category details"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "100ms" }}>
                    <TextField
                      fullWidth
                      label="Child Category Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter child category name"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "150ms" }}>
                    <TextField
                      fullWidth
                      label="Sort Order"
                      name="sortOrder"
                      type="number"
                      value={formData.sortOrder}
                      onChange={handleInputChange}
                      placeholder="Enter sort order"
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                {/* <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "200ms" }}>
                    <FormControl fullWidth required>
                      <InputLabel>Main Category</InputLabel>
                      <Select
                        name="mainCategory"
                        value={formData.mainCategory}
                        onChange={handleInputChange}
                        label="Main Category"
                        sx={{ borderRadius: "12px", height: "44px" }}
                      >
                        {mainCategories.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "250ms" }}>
                    <FormControl fullWidth required disabled={!formData.mainCategory}>
                      <InputLabel>Sub Category</InputLabel>
                      <Select
                        name="subCategory"
                        value={formData.subCategory}
                        onChange={handleInputChange}
                        label="Sub Category"
                        sx={{ borderRadius: "12px", height: "44px" }}
                      >
                        {subCategories.map((sub) => (
                          <MenuItem key={sub._id} value={sub._id}>
                            {sub.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Zoom>
                </Grid> */}

                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "300ms" }}>
                    <TextField
                      fullWidth
                      // label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter child category description"
                      multiline
                      rows={3}
                      variant="outlined"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          transition: "all 0.3s",
                          "&:hover": { transform: "translateX(5px)" },
                        },
                      }}
                    />
                  </Zoom>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "350ms" }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status === "active"}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: e.target.checked ? "active" : "inactive",
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#4caf50",
                            },
                          }}
                        />
                      }
                      label={
                        <MDTypography variant="button" fontWeight="medium">
                          {formData.status === "active" ? "Active" : "Inactive"}
                        </MDTypography>
                      }
                      sx={{ mt: 1 }}
                    />
                  </Zoom>
                </Grid>

                {/* Image Upload */}
                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "400ms" }}>
                    <Box>
                      <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                        Category Image (Optional)
                      </MDTypography>
                      <Paper
                        elevation={0}
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: "16px",
                          p: 2,
                          textAlign: "center",
                          bgcolor: "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            borderColor: "#2196f3",
                            bgcolor: "#f5f5f5",
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => document.getElementById("image-upload").click()}
                      >
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />

                        {imagePreview ? (
                          <Box sx={{ animation: `${bounceIn} 0.5s ease-out` }}>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                objectFit: "contain",
                                borderRadius: "12px",
                                marginBottom: "10px",
                              }}
                            />
                            <Box display="flex" justifyContent="center" gap={2}>
                              <MDButton
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImage();
                                }}
                                startIcon={<DeleteOutlineIcon />}
                                sx={{ borderRadius: "10px" }}
                              >
                                Remove
                              </MDButton>
                              <MDButton
                                variant="outlined"
                                color="info"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  document.getElementById("image-upload").click();
                                }}
                                startIcon={<CloudUploadIcon />}
                                sx={{ borderRadius: "10px" }}
                              >
                                Change
                              </MDButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box>
                            <CloudUploadIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
                            <MDTypography variant="body2" color="text">
                              Click to upload or drag and drop
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              PNG, JPG, JPEG, WEBP (Max 2MB)
                            </MDTypography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Zoom>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "450ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => {
                setOpenForm(false);
                resetForm();
              }}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
                color: "#111",
                borderColor: "#111",
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "500ms" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
            >
              {isSubmitting ? "PROCESSING..." : formMode === "add" ? "SAVE" : "UPDATE"}
            </MDButton>
          </Zoom>
        </DialogActions>
      </Dialog>

      {/* PREMIUM DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(239, 68, 68, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />

        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </Box>
              </Zoom>

              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Child Category?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>

              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      borderColor: "rgba(239, 68, 68, 0.4)",
                    },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    CHILD CATEGORY TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    &quot;{deleteChildCategoryName}&quot;
                  </MDTypography>
                </Paper>
              </Grow>

              <Fade in={deleteDialogOpen} timeout={800} style={{ transitionDelay: "200ms" }}>
                <Box sx={{ mt: 3 }}>
                  <MDTypography
                    variant="body2"
                    color="text"
                    fontWeight="medium"
                    sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                    This child category will be permanently removed
                  </MDTypography>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                borderColor: "#111",
                color: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>

          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeleteChildCategory}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>

        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ChildCategoryManagement;
